/**
 * PATH: packages/ui/src/components/settings/sender-profiles/SenderCompanyEditor.tsx
 * FILE_ID: UI-SETTINGS-SENDERS-COMPANY-029
 * PURPOSE: The company line on one sender-domain row — shows which CRM
 *          client the domain resolves to, with inline correct/attach
 *          ("edit Practicesale") writing POST /kanban/senders/company/.
 * WHY: Operator 2026-07-06 — "Why can I not edit the practicesale
 *      company". Roles had verdicts; the company had no surface.
 * FLOW: display (client_name | "Not in CRM") → pencil → input prefilled
 *       with client_name || suggested_label → save → onSave(name).
 * RELATED: UI-SETTINGS-SENDERS-ROW-027 (the mount), senderProfilesApi.ts
 *          setSenderCompany (the section wires it through onSave).
 */

import { useState } from 'react'
import { Building2, Check, Loader2, Pencil, X } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { SenderCompany } from './types'

export interface SenderCompanyEditorProps {
  domain: string
  company?: SenderCompany
  busy?: boolean
  /** Persist the corrected/attached company name for this domain. */
  onSave: (clientName: string) => void
}

export function SenderCompanyEditor({
  domain,
  company,
  busy,
  onSave,
}: SenderCompanyEditorProps) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')

  const resolved = (company?.client_name || '').trim()
  const suggested = (company?.suggested_label || '').trim()

  const startEdit = () => {
    setName(resolved || suggested)
    setEditing(true)
  }

  const commit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    onSave(trimmed)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 mt-1">
        <Building2 className="w-3 h-3 text-indigo-500 shrink-0" />
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') setEditing(false)
          }}
          maxLength={255}
          placeholder="Company name"
          aria-label={`Company for ${domain}`}
          className="flex-1 max-w-[240px] px-2 py-0.5 rounded-md text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        />
        <button
          onClick={commit}
          disabled={busy || !name.trim()}
          aria-label={`Save company for ${domain}`}
          className="p-1 rounded-md text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 disabled:opacity-40"
        >
          {busy ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
        </button>
        <button
          onClick={() => setEditing(false)}
          aria-label="Cancel company edit"
          className="p-1 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    )
  }

  return (
    <div className="group/company flex items-center gap-1.5 mt-1">
      <Building2
        className={cn(
          'w-3 h-3 shrink-0',
          resolved ? 'text-indigo-500' : 'text-slate-300 dark:text-slate-600',
        )}
      />
      <span
        className={cn(
          'text-xs truncate',
          resolved
            ? 'text-slate-600 dark:text-slate-300'
            : 'text-slate-400 italic',
        )}
      >
        {resolved || (suggested ? `Not in CRM — looks like ${suggested}` : 'Not in CRM')}
      </span>
      <button
        onClick={startEdit}
        disabled={busy}
        title={resolved ? `Correct company for ${domain}` : `Set company for ${domain}`}
        aria-label={`Edit company for ${domain}`}
        className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 opacity-0 group-hover/company:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-40"
      >
        <Pencil className="w-3 h-3" />
      </button>
    </div>
  )
}

export default SenderCompanyEditor
